import {
  Badge,
  Box,
  Button,
  Collapsible,
  HStack,
  Input,
  Switch,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { ChevronDown, ChevronRight, Plus, Send, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { ConnectionStatus, MqttQoS } from '@/lib/mqtt-types'

interface PublishPanelProps {
  status: ConnectionStatus
  onPublish: (
    topic: string,
    payload: string,
    qos: MqttQoS,
    retain: boolean,
    userProperties?: Record<string, string>,
  ) => void
  defaultOpen?: boolean
}

export function PublishPanel({ status, onPublish, defaultOpen = false }: PublishPanelProps) {
  const [topic, setTopic] = useState('')
  const [payload, setPayload] = useState('')
  const [qos, setQos] = useState<MqttQoS>(0)
  const [retain, setRetain] = useState(false)
  const [showUserProps, setShowUserProps] = useState(false)
  const [userProps, setUserProps] = useState<Array<{ key: string; value: string }>>([])
  const [isOpen, setIsOpen] = useState(defaultOpen)

  const isConnected = status === 'connected'

  // Update open state when defaultOpen changes
  useEffect(() => {
    setIsOpen(defaultOpen)
  }, [defaultOpen])

  const handlePublish = () => {
    if (!topic.trim()) return

    const props = userProps
      .filter((p) => p.key.trim() && p.value.trim())
      .reduce(
        (acc, p) => {
          acc[p.key.trim()] = p.value.trim()
          return acc
        },
        {} as Record<string, string>,
      )

    onPublish(topic.trim(), payload, qos, retain, Object.keys(props).length > 0 ? props : undefined)
  }

  const addUserProp = () => {
    setUserProps((prev) => [...prev, { key: '', value: '' }])
  }

  const removeUserProp = (index: number) => {
    setUserProps((prev) => prev.filter((_, i) => i !== index))
  }

  const updateUserProp = (index: number, field: 'key' | 'value', value: string) => {
    setUserProps((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)))
  }

  return (
    <Box borderWidth="1px" borderRadius="lg" bg="bg.panel">
      {/* Collapsible Header */}
      <Button
        variant="ghost"
        w="100%"
        justifyContent="space-between"
        onClick={() => setIsOpen(!isOpen)}
        p={4}
        h="auto"
        borderRadius={isOpen ? 'lg lg 0 0' : 'lg'}
      >
        <HStack gap={2}>
          {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <Text fontWeight="bold">Publish</Text>
          {!isConnected && (
            <Badge size="sm" colorPalette="gray" variant="subtle">
              Disconnected
            </Badge>
          )}
        </HStack>
      </Button>

      <Collapsible.Root open={isOpen}>
        <Collapsible.Content>
          <Box px={4} pb={4} mt={4}>
            <VStack gap={3} align="stretch">
              {/* Topic */}
              <Box>
                <Text fontSize="sm" mb={1}>
                  Topic
                </Text>
                <Input
                  size="sm"
                  placeholder="e.g., sensors/temperature"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  disabled={!isConnected}
                />
              </Box>

              {/* Payload */}
              <Box>
                <Text fontSize="sm" mb={1}>
                  Payload
                </Text>
                <Textarea
                  size="sm"
                  placeholder='e.g., {"temperature": 23.5, "unit": "C"}'
                  value={payload}
                  onChange={(e) => setPayload(e.target.value)}
                  disabled={!isConnected}
                  rows={4}
                  fontFamily="mono"
                  fontSize="xs"
                />
              </Box>

              {/* QoS & Retain */}
              <HStack gap={4}>
                <Box>
                  <Text fontSize="sm" mb={1}>
                    QoS
                  </Text>
                  <HStack gap={1}>
                    {([0, 1, 2] as MqttQoS[]).map((q) => (
                      <Button
                        key={q}
                        size="sm"
                        variant={qos === q ? 'solid' : 'outline'}
                        colorPalette={qos === q ? 'brand' : undefined}
                        onClick={() => setQos(q)}
                        disabled={!isConnected}
                        minW={10}
                      >
                        {q}
                      </Button>
                    ))}
                  </HStack>
                </Box>
                <HStack pt={5}>
                  <Text fontSize="sm">Retain</Text>
                  <Switch.Root
                    checked={retain}
                    onCheckedChange={(e) => setRetain(e.checked)}
                    disabled={!isConnected}
                  >
                    <Switch.HiddenInput />
                    <Switch.Control />
                  </Switch.Root>
                </HStack>
              </HStack>

              {/* User Properties (MQTT 5) */}
              <Box>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowUserProps(!showUserProps)}
                  w="100%"
                  justifyContent="flex-start"
                  disabled={!isConnected}
                >
                  {showUserProps ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <Text ml={1}>User Properties (MQTT 5)</Text>
                </Button>

                <Collapsible.Root open={showUserProps}>
                  <Collapsible.Content>
                    <VStack gap={2} align="stretch" mt={2}>
                      {userProps.map((prop, index) => (
                        <HStack key={index} gap={2}>
                          <Input
                            size="sm"
                            placeholder="Key"
                            value={prop.key}
                            onChange={(e) => updateUserProp(index, 'key', e.target.value)}
                            disabled={!isConnected}
                            flex={1}
                          />
                          <Input
                            size="sm"
                            placeholder="Value"
                            value={prop.value}
                            onChange={(e) => updateUserProp(index, 'value', e.target.value)}
                            disabled={!isConnected}
                            flex={1}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            colorPalette="red"
                            onClick={() => removeUserProp(index)}
                            disabled={!isConnected}
                          >
                            <X size={14} />
                          </Button>
                        </HStack>
                      ))}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={addUserProp}
                        disabled={!isConnected}
                      >
                        <Plus size={14} />
                        Add Property
                      </Button>
                    </VStack>
                  </Collapsible.Content>
                </Collapsible.Root>
              </Box>

              {/* Publish Button */}
              <Button
                colorPalette="green"
                onClick={handlePublish}
                disabled={!isConnected || !topic.trim()}
                mt={2}
              >
                <Send size={16} />
                Publish
              </Button>

              {/* Quick payloads */}
              <Box pt={2} borderTopWidth="1px">
                <Text fontSize="xs" color="content.secondary" mb={2}>
                  Quick payloads:
                </Text>
                <HStack gap={1} flexWrap="wrap">
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() =>
                      setPayload(JSON.stringify({ timestamp: new Date().toISOString() }))
                    }
                    disabled={!isConnected}
                  >
                    Timestamp
                  </Button>
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => setPayload(JSON.stringify({ value: Math.random().toFixed(2) }))}
                    disabled={!isConnected}
                  >
                    Random
                  </Button>
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => setPayload('ping')}
                    disabled={!isConnected}
                  >
                    Ping
                  </Button>
                </HStack>
              </Box>
            </VStack>
          </Box>
        </Collapsible.Content>
      </Collapsible.Root>
    </Box>
  )
}

export default PublishPanel
